"use client";

import { Mail, Sparkles, Users } from "lucide-react";

import { CookingUtensils } from "@/components/shared/CookingUtensils";
import type { TutorialSlide } from "@/components/tutorial/TutorialSheet";
import type { PageTutorialId } from "@/lib/storage";
import { cn } from "@/lib/utils";

type SlideFactory = (t: (key: string) => string) => TutorialSlide[];

const R2_BASE = "https://pub-b6d6afd32e1f4db4ad17e81459af1bac.r2.dev/recipes";

const TUTORIAL_RECIPES: {
  title: string;
  image: string;
}[] = [
  {
    title: "Miso Black Cod Rice",
    image: `${R2_BASE}/6424350bd9b91f22a6c1d2c5/hero/onkp6k9r.webp`,
  },
  {
    title: "Pappardelle Bolognese",
    image: `${R2_BASE}/6424409d550bf776bf1e9877/hero/oobx3pmt.webp`,
  },
  {
    title: "Lobster Bisque",
    image: `${R2_BASE}/64f20fdc1266f50d05f75d03/hero/x1p3k1gd.webp`,
  },
  {
    title: "Pork Thick Soup",
    image: `${R2_BASE}/64242a11758889d5dcfa4305/hero/56ra4f91.webp`,
  },
  {
    title: "Taiwanese Pork Cutlet",
    image: `${R2_BASE}/64da952b921a2441111d2bb8/hero/95jue6f5.webp`,
  },
  {
    title: "Crab Ravioli",
    image: `${R2_BASE}/6424415d550bf776bf1e9878/hero/fsk3f8qs.webp`,
  },
  {
    title: "French Toast",
    image: `${R2_BASE}/64244afa550bf776bf1e98a5/hero/8287n742.webp`,
  },
];

type DayData = {
  day: string;
  date: number;
  recipes: typeof TUTORIAL_RECIPES;
};

function TutorialDayCard({ day }: { day: DayData }) {
  const hasRecipes = day.recipes.length > 0;
  const firstRecipe = day.recipes[0];

  return (
    <div
      className={cn(
        "min-w-[160px] flex-1 flex flex-col overflow-hidden rounded-lg border",
        hasRecipes ? "border-border bg-card" : "border-border bg-card/50"
      )}
    >
      <div className="flex items-center justify-between px-2.5 py-1.5">
        <span
          className={cn(
            "text-xs font-medium uppercase",
            hasRecipes ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {day.day}
        </span>
        <span
          className={cn(
            "text-base font-semibold",
            hasRecipes ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {day.date}
        </span>
      </div>

      {hasRecipes ? (
        <>
          <TutorialImageGrid images={day.recipes} />
          {firstRecipe && (
            <div className="px-2.5 py-1.5">
              <p className="line-clamp-1 text-sm font-medium">{firstRecipe.title}</p>
              {day.recipes.length > 1 && (
                <p className="text-xs text-muted-foreground">+{day.recipes.length - 1} more</p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <CookingUtensils className="size-8 text-muted-foreground/30" />
        </div>
      )}
    </div>
  );
}

function TutorialImageGrid({ images }: { images: typeof TUTORIAL_RECIPES }) {
  if (images.length === 1) {
    return (
      <div className="relative aspect-square overflow-hidden">
        <img src={images[0].image} alt="" className="absolute inset-0 size-full object-cover" />
      </div>
    );
  }

  return (
    <div className="grid aspect-square grid-cols-2 gap-px overflow-hidden">
      {images.slice(0, 4).map((img, i) => (
        <div key={i} className="relative overflow-hidden">
          <img src={img.image} alt="" className="absolute inset-0 size-full object-cover" />
        </div>
      ))}
    </div>
  );
}

const dashboardSlides: SlideFactory = (t) => {
  const days: DayData[] = [
    { day: t("calendarMon"), date: 12, recipes: [TUTORIAL_RECIPES[0]] },
    { day: t("calendarTue"), date: 13, recipes: [] },
    {
      day: t("calendarWed"),
      date: 14,
      recipes: [TUTORIAL_RECIPES[1], TUTORIAL_RECIPES[2]],
    },
    { day: t("calendarThu"), date: 15, recipes: [TUTORIAL_RECIPES[3]] },
    {
      day: t("calendarFri"),
      date: 16,
      recipes: [TUTORIAL_RECIPES[4], TUTORIAL_RECIPES[5], TUTORIAL_RECIPES[6]],
    },
  ];

  return [
    {
      title: t("dashboardTitle"),
      description: t("dashboardDescription"),
      visual: (
        <div className="space-y-4 max-w-sm mx-auto">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("dashboardTodayLabel")}
          </p>
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="relative aspect-[21/9] bg-muted overflow-hidden">
              <img
                src={TUTORIAL_RECIPES[0].image}
                alt=""
                className="absolute inset-0 size-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <span className="absolute bottom-3 start-3 text-lg font-semibold text-white">
                {t("dashboardMealDinnerName")}
              </span>
            </div>
          </div>

          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("dashboardWeekLabel")}
          </p>
          <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-2">
            {days.map((day, i) => (
              <TutorialDayCard key={i} day={day} />
            ))}
          </div>
        </div>
      ),
    },
    {
      title: t("dashboardPrepTitle"),
      description: t("dashboardPrepDescription"),
      visual: (
        <div className="max-w-sm mx-auto">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
            {t("dashboardPrepAheadLabel")}
          </p>
          <div className="rounded-xl border border-border bg-card">
            <div className="p-4">
              <ol className="relative ms-1.5">
                {[
                  { label: t("dashboardPrepDay1"), text: t("dashboardPrepTip1") },
                  { label: t("dashboardPrepDay2"), text: t("dashboardPrepTip2") },
                ].map((item, i, arr) => (
                  <li key={i} className={cn("relative ps-5", i < arr.length - 1 && "pb-6")}>
                    <div className="absolute -start-1.5 top-1 z-10 size-3 rounded-full border-2 border-primary bg-card" />
                    {i < arr.length - 1 && (
                      <div className="absolute start-0 top-2.5 -bottom-2.5 w-px bg-border" />
                    )}
                    <p className="font-semibold leading-tight text-primary">{item.label}</p>
                    <p className="mt-1">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="flex items-center border-t border-border px-4 py-2.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Sparkles className="size-3 shrink-0" />
                <span>{t("dashboardPrepCredits")}</span>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];
};

const recipesSlides: SlideFactory = (t) => [
  {
    title: t("recipesAiTitle"),
    description: t("recipesAiDescription"),
    visual: (
      <div className="max-w-sm mx-auto">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-2 border-b border-border px-3 py-3">
            <Sparkles className="size-4 text-primary" />
            <span className="text-sm font-medium">{t("recipesAiChatLabel")}</span>
          </div>
          <div className="px-3 py-4 space-y-3">
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl bg-primary px-3.5 py-2.5 text-sm text-primary-foreground">
                {t("recipesAiPrompt1")}
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-base text-foreground">
                {t("recipesAiResponse1")}
              </div>
            </div>
          </div>
          <div className="border-t border-border px-3 py-3">
            <div className="flex items-center gap-2 rounded-lg border border-input-border bg-input-bg px-3 py-2 text-sm text-muted-foreground">
              <Sparkles className="size-4 shrink-0" />
              <span>{t("recipesAiPlaceholder")}</span>
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

const calendarSlides: SlideFactory = (t) => [
  {
    title: t("calendarSuggestTitle"),
    description: t("calendarSuggestDescription"),
    visual: (
      <div className="mx-auto max-w-sm">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border px-3 py-3">
            <Sparkles className="size-4 text-primary" />
            <span className="text-sm font-medium">{t("calendarSuggestLabel")}</span>
          </div>
          <div className="space-y-3 px-3 py-4">
            <div className="whitespace-pre-wrap rounded-lg border border-border bg-muted/50 px-3 py-2.5 text-sm text-foreground">
              {t("calendarSuggestPreview")}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["calendarSuggestChip1", "calendarSuggestChip2", "calendarSuggestChip3"].map(
                (key) => (
                  <span
                    key={key}
                    className="rounded-full border border-input-border px-3 py-1 text-xs font-medium text-muted-foreground"
                  >
                    {t(key)}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: t("calendarShareTitle"),
    description: t("calendarShareDescription"),
    visual: (
      <div className="max-w-sm mx-auto">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-2 border-b border-border px-3 py-3">
            <Users className="size-4 text-primary" />
            <span className="text-sm font-medium">{t("calendarShareLabel")}</span>
          </div>
          <div className="px-3 py-4 space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-input-border bg-input-bg px-3 py-2 text-sm text-muted-foreground">
              <Mail className="size-4 shrink-0" />
              <span>{t("calendarShareEmailPlaceholder")}</span>
            </div>
            <div className="space-y-2 pt-1">
              {[
                { email: "alex@email.com", status: t("calendarShareAccepted"), accepted: true },
                { email: "sam@email.com", status: t("calendarSharePending"), accepted: false },
              ].map((person) => (
                <div
                  key={person.email}
                  className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2.5"
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm">{person.email}</span>
                    <span
                      className={cn(
                        "text-xs",
                        person.accepted
                          ? "text-green-600 dark:text-green-500"
                          : "text-yellow-600 dark:text-yellow-500"
                      )}
                    >
                      {person.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

const slideFactories: Record<PageTutorialId, SlideFactory> = {
  dashboard: dashboardSlides,
  recipes: recipesSlides,
  calendar: calendarSlides,
};

export function getPageTutorialSlides(
  page: PageTutorialId,
  t: (key: string) => string
): TutorialSlide[] {
  return slideFactories[page](t);
}
